import { hashCanonical, hashCanonicalB64 } from './hash.js';
/**
 * Project checkout statements (docs/ux/workflow-simplification.md §E2, docs/e2e.md).
 *
 * Cloning a repository onto a machine, or creating a new one, is a passkey-approved act. The PWA
 * builds ONE statement naming every chosen machine, the user approves it with ONE passkey tap,
 * and each machine receives its own device-signed (encrypted) `project.checkout` command that
 * carries the same statement and assertion. The statement satisfies the risky-kind step-up for
 * that command (no extra tap per machine). It is plaintext-safe: ids and digests only; the
 * remote itself travels inside the encrypted command payload and is bound by `remoteDigest`.
 */
export const CHECKOUT_DOMAIN = {
    statement: 'viko/project-checkout/v1',
    remote: 'viko/project-checkout-remote/v1',
};
/** Command kinds whose passkey step-up can be satisfied by a checkout statement. */
export const CHECKOUT_STEP_UP_KINDS = ['project.checkout'];
export const MAX_CHECKOUT_TTL_MS = 15 * 60_000;
export const DEFAULT_CHECKOUT_TTL_MS = 15 * 60_000;
export const MAX_CHECKOUT_MACHINES = 8;
export const CHECKOUT_CLOCK_SKEW_MS = 60_000;
/**
 * Canonical git remote: `git@github.com:o/r.git` ≡ `https://github.com/o/r` ≡
 * `ssh://git@github.com/o/r` → `github.com/o/r`. Credentials, ports, `.git` and trailing
 * slashes are dropped; hosts are lower-cased, and GitHub paths too (GitHub is case-insensitive).
 */
export function canonicalGitRemote(input) {
    let s = input.trim();
    let host = '';
    let rest = '';
    const scp = /^(?:[^@/\s]+@)?([^:/\s]+):(?!\/)(.+)$/.exec(s);
    const url = /^[a-z][a-z0-9+.-]*:\/\/(?:[^@/]*@)?([^/:]+)(?::\d+)?\/(.+)$/i.exec(s);
    if (url) {
        host = url[1];
        rest = url[2];
    }
    else if (scp && !/^[A-Za-z]$/.test(scp[1])) {
        host = scp[1];
        rest = scp[2];
    }
    else {
        return s.replace(/[\\/]+$/, '');
    }
    host = host.toLowerCase();
    s = rest
        .replace(/\/+$/, '')
        .replace(/\.git$/i, '')
        .replace(/^\/+/, '');
    if (host === 'github.com' || host.endsWith('.github.com'))
        s = s.toLowerCase();
    return `${host}/${s}`;
}
/** Digest the statement binds: the canonical remote, or the repo to create. */
export function checkoutRemoteDigest(target) {
    const value = 'remote' in target
        ? { remote: canonicalGitRemote(target.remote) }
        : {
            create: {
                provider: target.create.provider ?? 'github',
                owner: target.create.owner.toLowerCase(),
                name: target.create.name.toLowerCase(),
            },
        };
    return hashCanonicalB64(CHECKOUT_DOMAIN.remote, value);
}
export function createCheckoutStatement(input) {
    const issuedAt = input.now ?? Date.now();
    const s = {
        version: 1,
        type: 'viko.project-checkout',
        accountId: input.accountId,
        statementId: input.statementId ?? globalThis.crypto.randomUUID(),
        projectId: input.projectId,
        machineIds: [...new Set(input.machineIds)].sort(),
        remoteDigest: input.remoteDigest,
        issuedAt,
        expiresAt: issuedAt + Math.min(input.ttlMs ?? DEFAULT_CHECKOUT_TTL_MS, MAX_CHECKOUT_TTL_MS),
        deviceKeyId: input.deviceKeyId,
    };
    if (input.configAuthor)
        s.configAuthor = input.configAuthor;
    return s;
}
/** WebAuthn challenge (32 bytes) for the one passkey tap that approves every listed checkout. */
export function createCheckoutChallenge(statement) {
    return hashCanonical(CHECKOUT_DOMAIN.statement, statement);
}
const str = (v, max = 256) => typeof v === 'string' && v.length > 0 && v.length <= max;
const int = (v) => Number.isSafeInteger(v) && v >= 0;
export function isCheckoutStatement(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return false;
    const s = v;
    const allowed = new Set([
        'version',
        'type',
        'accountId',
        'statementId',
        'projectId',
        'machineIds',
        'remoteDigest',
        'configAuthor',
        'issuedAt',
        'expiresAt',
        'deviceKeyId',
    ]);
    if (Object.keys(s).some((k) => !allowed.has(k)))
        return false;
    const ids = s.machineIds;
    return (s.version === 1 &&
        s.type === 'viko.project-checkout' &&
        str(s.accountId) &&
        str(s.statementId) &&
        str(s.projectId) &&
        Array.isArray(ids) &&
        ids.length >= 1 &&
        ids.length <= MAX_CHECKOUT_MACHINES &&
        ids.every((m) => str(m, 128)) &&
        new Set(ids).size === ids.length &&
        str(s.remoteDigest, 64) &&
        (s.configAuthor === undefined || (str(s.configAuthor, 128) && ids.includes(s.configAuthor))) &&
        int(s.issuedAt) &&
        int(s.expiresAt) &&
        str(s.deviceKeyId));
}
/**
 * Verify the checkout approval embedded in a `project.checkout` payload, for THIS machine:
 * shape, account, machine ∈ machineIds, project and remote bound to the payload, the command's
 * device built the statement, validity window (≤15 min), unused statementId, and a passkey
 * assertion from a pinned credential over the statement.
 */
export async function verifyCheckoutApproval(input, trust, opts) {
    const s = input?.statement;
    if (!isCheckoutStatement(s) || typeof input.assertion !== 'object' || !input.assertion)
        return { ok: false, reason: 'malformed' };
    const now = opts.now ?? Date.now();
    const skew = opts.clockSkewMs ?? CHECKOUT_CLOCK_SKEW_MS;
    if (s.accountId !== opts.accountId)
        return { ok: false, reason: 'wrong-account' };
    if (!s.machineIds.includes(opts.machineId))
        return { ok: false, reason: 'machine-not-listed' };
    if (s.projectId !== input.projectId)
        return { ok: false, reason: 'project-mismatch' };
    if (s.deviceKeyId !== opts.deviceKeyId)
        return { ok: false, reason: 'device-mismatch' };
    if (!!input.remote === !!input.create)
        return { ok: false, reason: 'malformed', detail: 'exactly one of remote/create' };
    const target = input.remote ? { remote: input.remote } : { create: input.create };
    if ((await checkoutRemoteDigest(target)) !== s.remoteDigest)
        return { ok: false, reason: 'remote-mismatch' };
    if (s.expiresAt <= s.issuedAt)
        return { ok: false, reason: 'malformed', detail: 'window' };
    if (s.expiresAt - s.issuedAt > MAX_CHECKOUT_TTL_MS)
        return { ok: false, reason: 'ttl-too-long' };
    if (s.issuedAt > now + skew)
        return { ok: false, reason: 'not-yet-valid' };
    if (now > s.expiresAt + skew)
        return { ok: false, reason: 'expired' };
    if (opts.isUsed?.(s.statementId))
        return { ok: false, reason: 'replay' };
    const approval = await trust.verifyPasskeyApproval(input.assertion, await createCheckoutChallenge(s), 
    // One assertion is replayed to every listed machine; the signCount can repeat.
    {});
    if (!approval.ok)
        return { ok: false, reason: 'bad-approval', detail: approval.reason };
    return { ok: true, statement: s, credentialId: approval.credential.credentialId };
}
//# sourceMappingURL=checkout.js.map